import { readAwsBucketName } from './prevention-aws-bucket-name';
import {
  EMAIL_AUTH_MECHANISMS,
  type EmailAuthMechanism,
} from './prevention-config-details.schema';

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const stripTrailingDot = (value: string): string => value.replace(/\.$/, '');

export const decodeRoute53Name = (name: string): string =>
  stripTrailingDot(
    name.replace(/\\(\d{3})/g, (_match, octal: string) =>
      String.fromCharCode(Number.parseInt(octal, 8)),
    ),
  ).toLowerCase();

export const normalizeDnsName = (name: string): string =>
  decodeRoute53Name(name.trim());

export const deriveEmailAuthRecordName = ({
  target,
  mechanism,
  selector,
}: {
  target: string;
  mechanism?: EmailAuthMechanism | string | null;
  selector?: string | null;
}): string | null => {
  const apex = normalizeDnsName(target);

  if (!apex) {
    return null;
  }

  if (mechanism === EMAIL_AUTH_MECHANISMS.DMARC) {
    return `_dmarc.${apex}`;
  }

  if (mechanism === EMAIL_AUTH_MECHANISMS.DKIM) {
    const trimmedSelector = selector?.trim();

    if (!trimmedSelector) {
      return null;
    }

    return `${trimmedSelector}._domainkey.${apex}`;
  }

  return apex;
};

export const deriveConfigRecordName = ({
  change,
  target,
  details,
}: {
  change?: string | null;
  target?: string | null;
  details?: Record<string, unknown>;
}): string | null => {
  const resolvedTarget = readString(target);

  if (!resolvedTarget) {
    return null;
  }

  if (change === 'emailAuth') {
    return deriveEmailAuthRecordName({
      target: resolvedTarget,
      mechanism: readString(details?.mechanism),
      selector: readString(details?.selector),
    });
  }

  return normalizeDnsName(resolvedTarget);
};

export const deriveAwsListenerArn = ({
  target,
  scopeRef,
  enrichedListenerArn,
}: {
  target?: string | null;
  scopeRef?: string | null;
  enrichedListenerArn?: string | null;
}): string | null => {
  const candidates = [scopeRef, enrichedListenerArn, target];

  for (const candidate of candidates) {
    const value = readString(candidate);

    if (value && /^arn:aws:elasticloadbalancing:/i.test(value)) {
      return value;
    }
  }

  return null;
};

export const deriveAwsBucketNameFromTarget = ({
  target,
}: {
  target: string;
}): string | null => readAwsBucketName({ target });

export const pickLongestSuffixHostedZone = ({
  recordName,
  zones,
}: {
  recordName: string;
  zones: readonly { id: string; name: string }[];
}): { id: string; name: string } | null => {
  const normalizedRecord = normalizeDnsName(recordName);
  const matches = zones
    .map((zone) => ({
      id: zone.id.replace(/^\/hostedzone\//, ''),
      name: normalizeDnsName(zone.name),
    }))
    .filter(
      (zone) =>
        normalizedRecord === zone.name ||
        normalizedRecord.endsWith(`.${zone.name}`),
    )
    .sort((left, right) => right.name.length - left.name.length);

  return matches[0] ?? null;
};
